import { alpha, Box, Theme } from '@mui/material';
import { useAppBuilderContext } from 'app/app/editor/context';
import LayoutNode from 'app/app/editor/tabs/elements/layout/LayoutNode';
import { useLayoutContextMenu } from 'app/app/editor/tabs/elements/layout/useLayoutContextMenu';
import FlatList from 'app/app/editor/tabs/elements/layout/FlatList';
import { DndProvider } from 'react-dnd';
import {
    getBackendOptions,
    MultiBackend,
    NodeModel,
    Tree as MinoruTree,
    TreeMethods,
} from '@minoru/react-dnd-treeview';
import React, { useEffect, useMemo, useRef } from 'react';
import { IComponentNode } from 'app/app/editor/index.functions';
import insertNodeIntoPage from 'app/app/editor/page/insertNodeIntoPage';
import { getNodeByUid } from 'app/app/editor/page/getNodeByUid';
import styles from 'components/common/tree/Tree.styles';
import { useDeleteKeyListener } from './useDeleteKeyListener';
type TreeType = NodeModel<IComponentNode>;

function getAncestryChain(tree: Array<TreeType>, id: string | null) {
    const chain = [];
    let currentId = id;

    // Create a map for quick lookup
    const nodeMap = new Map();
    tree.forEach((node) => {
        nodeMap.set(node.id, node);
    });

    // Traverse up the tree
    while (currentId !== null && currentId !== undefined) {
        const node = nodeMap.get(currentId);

        if (!node) {
            break; // Node not found
        }

        chain.push(currentId);

        // Move to parent (0 typically represents root/no parent)
        currentId = node.parent === 0 ? null : node.parent;
    }

    return chain;
}

export default function LayoutTree({
    componentUid,
    panelWidth,
}: {
    componentUid: string | null;
    panelWidth: number | null;
}) {
    const ctx = useAppBuilderContext();
    const layout = ctx.page?.layout;

    const { handleOpenContextMenu, contextMenu } = useLayoutContextMenu();

    const treeData: Array<TreeType> = useMemo(() => {
        const nodeToModel = (node: IComponentNode, parentNode?: IComponentNode): Array<TreeType> => {
            const result: Array<TreeType> = [
                {
                    id: node.params.uid,
                    parent: parentNode ? parentNode.params.uid : 0,
                    droppable: node.type == 'container',
                    text: node.params.uid,
                    data: node,
                },
            ];

            return result.concat(
                node.elements?.map((n) => nodeToModel(n, node)).reduce((a, b) => a.concat(b), []) ?? [],
            );
        };
        return nodeToModel(layout);
    }, [layout]);

    useDeleteKeyListener({
        componentUid,
        confirmDeletion: false,
        preventDeleteRoot: true,
    });

    useEffect(() => {
        const ids = getAncestryChain(treeData, componentUid);
        if (ids.length) {
            ref.current?.open(ids);
        }
    }, [componentUid, treeData]);

    const handleDrop = (source: IComponentNode, target: IComponentNode, index?: number) => {
        const node = getNodeByUid(ctx.page.layout, source.params.uid);
        if (!node) {
            return;
        }
        const newPage = insertNodeIntoPage(ctx.page, node, target.params.uid, index, true);
        ctx.setPage(newPage, false);
        ctx.sendMessage({ name: 'reloadPage' });
    };

    const ref = useRef<TreeMethods>(null);

    if (!layout) {
        return null;
    }
    return (
        <Box>
            {contextMenu}
            {ctx.withTemplate ? (
                <FlatList componentUid={componentUid} node={layout} />
            ) : (
                <Box sx={styles.holder}>
                    <DndProvider backend={MultiBackend} options={getBackendOptions()}>
                        <MinoruTree
                            sort={false}
                            insertDroppableFirst={false}
                            tree={treeData}
                            ref={ref}
                            rootId={0}
                            canDrag={(node) => {
                                return node?.data?.subtype != 'page_menu';
                            }}
                            canDrop={(tree, { dragSource, dropTargetId }) => {
                                if (dragSource?.parent === dropTargetId) {
                                    return true;
                                }
                            }}
                            onDrop={(_: any, options) => {
                                if (!options?.dragSource?.data || !options.dropTarget?.data) {
                                    return;
                                }
                                handleDrop(options.dragSource.data, options.dropTarget.data, options.relativeIndex);
                            }}
                            initialOpen={true}
                            placeholderRender={(node, { depth }) => <Placeholder depth={depth} />}
                            render={(node: TreeType, params) => {
                                if (!node.data) {
                                    return <></>;
                                }
                                return (
                                    <LayoutNode
                                        panelWidth={panelWidth}
                                        handleOpenContextMenu={handleOpenContextMenu}
                                        node={node.data}
                                        componentUid={componentUid}
                                        isOpen={params.isOpen}
                                        toggleOpen={params.onToggle}
                                    />
                                );
                            }}
                            classes={{
                                root: 'tree-mi',
                                container: 'tree-mi__list',
                                listItem: 'tree-mi__item',
                                dropTarget: 'tree-mi__drop-target',
                                draggingSource: 'tree-mi__dragging-source',
                                placeholder: 'tree-mi__placeholder',
                            }}
                        />
                    </DndProvider>
                </Box>
            )}
        </Box>
    );
}

function Placeholder(props: { depth: number }) {
    return (
        <Box
            sx={{
                height: '16px',
                left: props.depth * 24 + 'px',
                border: (theme: Theme) => `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
            }}
        ></Box>
    );
}
